import { useState } from "react";
import { Loader, Lock, AlertCircle } from "lucide-react";
import PasswordRequirements from "../../../components/PasswordRequirements";
import { isPasswordValid } from "../../../utils/passwordPolicy";

/**
 * Blocking modal shown right after a phone-only account's first successful
 * login (no email on file to send a reset link to, so the server lets the
 * login through and flags `mustChangePassword` instead). Unlike
 * ResetPasswordModal there's no "Cancel" that dismisses it — a new password
 * must actually be set before the rest of the login flow (profile fetch,
 * navigation into the app) resumes. The only way out is "Log out instead",
 * which drops the pending session rather than silently letting them in on
 * the shared/default password.
 */
const ForceChangePasswordModal = ({
  isOpen,
  t,
  language = "en",
  styles,
  isLoading,
  error,
  onSubmit,
  onLogoutInstead,
}) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localError, setLocalError] = useState("");

  if (!isOpen) return null;

  const displayError = localError || error;

  const handleSubmit = () => {
    if (isLoading) return;
    // Same policy as the emailed reset link and the Settings modal — and the
    // one the server now enforces (user-service/src/utils/passwordPolicy.js).
    if (!isPasswordValid(newPassword)) {
      setLocalError(t.forcePasswordPolicy);
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError(t.forcePasswordMismatch);
      return;
    }
    setLocalError("");
    onSubmit(newPassword);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header — no close button; this step is mandatory */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{t.forcePasswordTitle}</h2>
          <p style={styles.modalSubtitle}>{t.forcePasswordSubtitle}</p>
        </div>

        <div style={styles.modalForm}>
          {/* New Password */}
          <div style={styles.modalInputGroup}>
            <label style={styles.modalLabel}>{t.forcePasswordNewLabel}</label>
            <div style={styles.modalInputWrapper}>
              <Lock style={styles.modalIcon} />
              <input
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (localError) setLocalError("");
                }}
                onKeyPress={handleKeyPress}
                placeholder={t.forcePasswordNewPlaceholder}
                disabled={isLoading}
                style={{ ...styles.modalInput, opacity: isLoading ? 0.6 : 1 }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#084d98";
                  e.target.style.backgroundColor = "#FFFFFF";
                  e.target.style.boxShadow = "0 0 0 3px rgba(8, 77, 152, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E5E7EB";
                  e.target.style.backgroundColor = "#F9FAFB";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>

          <PasswordRequirements
            password={newPassword}
            language={language}
            theme="light"
          />

          {/* Confirm Password */}
          <div style={styles.modalInputGroup}>
            <label style={styles.modalLabel}>{t.forcePasswordConfirmLabel}</label>
            <div style={styles.modalInputWrapper}>
              <Lock style={styles.modalIcon} />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (localError) setLocalError("");
                }}
                onKeyPress={handleKeyPress}
                placeholder={t.forcePasswordConfirmPlaceholder}
                disabled={isLoading}
                style={{ ...styles.modalInput, opacity: isLoading ? 0.6 : 1 }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#084d98";
                  e.target.style.backgroundColor = "#FFFFFF";
                  e.target.style.boxShadow = "0 0 0 3px rgba(8, 77, 152, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E5E7EB";
                  e.target.style.backgroundColor = "#F9FAFB";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>

          {displayError && (
            <div style={{ ...styles.modalStatusMessage, ...styles.modalErrorMessage }}>
              <AlertCircle size={20} style={{ flexShrink: 0, marginTop: "2px" }} />
              <span>{displayError}</span>
            </div>
          )}

          <div style={styles.modalButtonGroup}>
            <button
              type="button"
              onClick={onLogoutInstead}
              disabled={isLoading}
              style={{
                ...styles.modalButton,
                ...styles.modalSecondaryButton,
                opacity: isLoading ? 0.5 : 1,
                cursor: isLoading ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!isLoading) e.currentTarget.style.backgroundColor = "#E5E7EB";
              }}
              onMouseLeave={(e) => {
                if (!isLoading) e.currentTarget.style.backgroundColor = "#F3F4F6";
              }}
            >
              {t.forcePasswordLogoutInstead}
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              style={{
                ...styles.modalButton,
                ...styles.modalPrimaryButton,
                opacity: isLoading ? 0.7 : 1,
                cursor: isLoading ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = "#063a73";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(8, 77, 152, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = "#084d98";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }
              }}
            >
              {isLoading ? (
                <>
                  <Loader size={18} className="animate-spin" />
                  {t.sending}
                </>
              ) : (
                t.forcePasswordSubmit
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForceChangePasswordModal;
